import { Link, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  Database,
  History,
  Home,
  MessageSquare,
  Plus,
  Settings,
  Shield,
  Users,
  Zap,
} from 'lucide-react'
import type { DocumentCollectionsQuery } from '@/graphql/queries/documentCollectionsQuery.gql.generated'
import { useCommunicationNotifications } from '@/features/communications/notifications/notification-context'
import { getDocumentCollectionIconOption } from '@/entities/document-collection/document-collection-icons'
import { useCurrentUserProfile } from '@/entities/user/model/use-current-user-profile'
import { useWorkspaceFeatureDecisionsQuery } from '@/graphql/queries/workspaceFeatureDecisionsQuery.gql.generated'
import { buttonVariants } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  isAutomationsFeatureDecisionEnabled,
  isCommunicationsFeatureDecisionEnabled,
} from '@/shared/lib/feature-decisions'

function useNavLinks() {
  const { t } = useTranslation()

  return [
    { to: '/', label: t('shell:home'), icon: Home },
    {
      to: '/document-collections',
      label: t('shell:documentCollections'),
      icon: Database,
    },
    {
      to: '/communications/contacts',
      label: t('shell:communications'),
      icon: MessageSquare,
    },
    {
      to: '/team/members',
      label: t('shell:team'),
      icon: Users,
    },
    {
      to: '/audit',
      label: t('shell:audit'),
      icon: History,
    },
    {
      to: '/settings',
      label: t('shell:settings'),
      icon: Settings,
    },
    {
      to: '/automations',
      label: t('shell:automations'),
      icon: Zap,
    },
  ] as const
}

type DocumentCollectionLinkRow =
  DocumentCollectionsQuery['documentCollections'][number]

function NavLink({
  to,
  label,
  icon: Icon,
  onClick,
}: {
  to: any
  label: string
  icon: typeof Home
  onClick: () => void
}) {
  const { total } = useCommunicationNotifications()

  return (
    <Link
      to={to}
      onClick={onClick}
      className="mb-2 flex items-center gap-3 rounded-lg p-3 text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      activeProps={{
        className:
          'mb-2 flex items-center gap-3 rounded-lg bg-sidebar-primary p-3 text-sidebar-primary-foreground transition-colors hover:bg-sidebar-primary/90',
      }}
    >
      <Icon size={20} />
      <span className="font-medium">{label}</span>
      {to === '/communications/contacts' && total > 0 && (
        <span className="ml-auto rounded-full bg-primary px-2 text-primary-foreground">
          {total}
        </span>
      )}
    </Link>
  )
}

function CollectionNavLink({
  collection,
  onClick,
}: {
  collection: DocumentCollectionLinkRow
  onClick: () => void
}) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const isActive = pathname.startsWith(`/document-collection/${collection.id}`)
  const { Icon } = getDocumentCollectionIconOption(collection.icon)

  return (
    <Link
      to="/document-collection/$id"
      params={{ id: collection.id }}
      onClick={onClick}
      className={cn(
        'mb-2 flex items-center gap-3 rounded-lg p-3 text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        isActive &&
          'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90',
      )}
    >
      <Icon size={18} />
      <span className="min-w-0 truncate font-medium">{collection.name}</span>
    </Link>
  )
}

function DocumentCollectionsNavSection({
  collections,
  onCollectionClick,
  onCreateCollectionClick,
}: {
  collections: Array<DocumentCollectionLinkRow>
  onCollectionClick: () => void
  onCreateCollectionClick: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="mt-4 flex flex-col">
      <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-sidebar-foreground/50">
        {t('shell:documentCollections')}
      </div>
      <div aria-label={t('shell:documentCollections')} className="pr-1">
        {collections.map((collection) => (
          <CollectionNavLink
            key={collection.id}
            collection={collection}
            onClick={onCollectionClick}
          />
        ))}
        <button
          type="button"
          onClick={onCreateCollectionClick}
          className={cn(
            buttonVariants({ variant: 'ghost' }),
            'mb-2 flex w-full items-center justify-start gap-3 rounded-lg px-3 text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          )}
        >
          <Plus size={18} />
          <span className="font-medium">
            {t('documentCollections:createNewCollection')}
          </span>
        </button>
      </div>
    </div>
  )
}

export interface NavContentProps {
  collections: Array<DocumentCollectionLinkRow>
  onNavigate: () => void
  onCreateCollection: () => void
  showMainNavigation?: boolean
}

function useAutomationsFeatureEnabled() {
  const { currentUser } = useCurrentUserProfile()
  const workspaceId = currentUser?.workspaceId

  const decisionsQuery = useWorkspaceFeatureDecisionsQuery({
    skip: !workspaceId,
    variables: { workspaceId: workspaceId ?? '' },
  })

  const canReadAutomations = currentUser?.can('read', 'automation') ?? false
  const isFeatureEnabled = isAutomationsFeatureDecisionEnabled(
    decisionsQuery.data?.workspaceFeatureDecisions,
  )

  return canReadAutomations && isFeatureEnabled
}

function useCommunicationsFeatureEnabled() {
  const { currentUser } = useCurrentUserProfile()
  const workspaceId = currentUser?.workspaceId
  const decisionsQuery = useWorkspaceFeatureDecisionsQuery({
    skip: !workspaceId,
    variables: { workspaceId: workspaceId ?? '' },
  })

  return (
    (currentUser?.can('read', 'communication') ?? false) &&
    isCommunicationsFeatureDecisionEnabled(
      decisionsQuery.data?.workspaceFeatureDecisions,
    )
  )
}

export function NavContent({
  collections,
  onNavigate,
  onCreateCollection,
  showMainNavigation = true,
}: NavContentProps) {
  const { currentUser } = useCurrentUserProfile()
  const { t } = useTranslation()
  const canReadSettings = currentUser?.can('read', 'workspaceSettings') ?? false
  const canReadAudit = currentUser?.can('read', 'auditLog') ?? false
  const showAutomations = useAutomationsFeatureEnabled()
  const showCommunications = useCommunicationsFeatureEnabled()

  const baseProductLinks = useNavLinks().filter(
    (link) => link.to !== '/audit' || canReadAudit,
  )

  const automationsFilteredLinks = baseProductLinks.filter(
    (link) => link.to !== '/automations' || showAutomations,
  )

  const featureFilteredLinks = automationsFilteredLinks.filter(
    (link) => link.to !== '/communications/contacts' || showCommunications,
  )

  const productLinks = canReadSettings
    ? [
        ...featureFilteredLinks,
        {
          to: '/administration' as const,
          label: t('shell:administration'),
          icon: Shield,
        },
      ]
    : featureFilteredLinks

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav className="flex flex-1 flex-col p-4">
        <div
          data-testid="main-navigation-section"
          data-state={showMainNavigation ? 'expanded' : 'collapsed'}
          aria-hidden={!showMainNavigation}
          className={cn(
            'grid overflow-hidden transition-[grid-template-rows,opacity,margin-bottom] duration-200 ease-in-out motion-reduce:transition-none',
            showMainNavigation
              ? 'mb-0 grid-rows-[1fr] opacity-100'
              : 'pointer-events-none mb-0 grid-rows-[0fr] opacity-0',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            {productLinks.map((link) => (
              <NavLink key={link.to} {...link} onClick={onNavigate} />
            ))}
          </div>
        </div>
        <DocumentCollectionsNavSection
          collections={collections}
          onCollectionClick={onNavigate}
          onCreateCollectionClick={onCreateCollection}
        />
      </nav>
    </div>
  )
}
